import { useEffect, useState, useCallback, useMemo } from "react";
import api, { apiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { EmptyState, ErrorState, CardsSkeleton } from "@/components/States";
import { initials } from "@/lib/format";
import { todayLabel, CHANNEL_META, ACTION_META, TYPE_META, statusBadge, LEAD_LABEL } from "@/lib/format";
import { Check, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import FirstSteps from "@/components/FirstSteps";
import ProfileNudge from "@/components/ProfileNudge";
import ProspectDrawer from "@/components/ProspectDrawer";
import { ACTION_LABEL, ACTION_TEXT, STAGE_LABEL, TODAY_GROUPS, dayLabel } from "@/lib/plan";

const CHIP_DEFS = [
  { key: "analyzed", label: "Perfiles analizados" },
  { key: "contacted", label: "Contactos iniciados" },
  { key: "conversations", label: "Conversaciones" },
  { key: "clients", label: "Clientes" },
];

const FILTERS = [
  { k: "", label: "Todas" },
  { k: "instagram", label: "Instagram" },
  { k: "linkedin", label: "LinkedIn" },
];

function greeting() {
  const h = new Date().getHours();
  if (h < 6) return "Buenas noches";
  if (h < 13) return "Buenos días";
  if (h < 21) return "Buenas tardes";
  return "Buenas noches";
}

function sparkPath(vals) {
  if (!vals || !vals.length) return "M0 24 L100 12";
  const max = Math.max(1, ...vals);
  const n = vals.length;
  return vals.map((v, i) => {
    const x = (i / Math.max(1, n - 1)) * 100;
    const y = 26 - (v / max) * 22;
    return `${i === 0 ? "M" : "L"}${x.toFixed(0)} ${y.toFixed(0)}`;
  }).join(" ");
}

const AV = { width: 34, height: 34, borderRadius: 10, display: "grid", placeItems: "center", fontSize: 12, fontWeight: 600, background: "linear-gradient(135deg,#2a2416,#14110a)", color: "var(--ca-gold-hi)", border: "1px solid rgba(224,185,112,.2)", flex: "none" };

function NetIcon({ channel }) {
  const base = { width: 22, height: 22, borderRadius: 6, display: "inline-grid", placeItems: "center", fontSize: 11, fontWeight: 700, flex: "none", verticalAlign: "middle" };
  if (channel === "instagram") return <span style={{ ...base, background: "linear-gradient(135deg,#f9ce34,#ee2a7b,#6228d7)", color: "#fff" }}>◎</span>;
  if (channel === "linkedin") return <span style={{ ...base, background: "#0a66c2", color: "#fff" }}>in</span>;
  if (channel === "facebook") return <span style={{ ...base, background: "#1877f2", color: "#fff" }}>f</span>;
  return <span style={{ ...base, background: "var(--ca-surface-2)", color: "var(--ca-gold)" }}>◎</span>;
}

function ProgressRing({ done, total }) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <div data-testid="today-progress-ring" style={{ width: 64, height: 64, borderRadius: "50%", display: "grid", placeItems: "center", flex: "none", background: `conic-gradient(var(--ca-gold) ${pct}%, rgba(255,255,255,.07) 0)` }}>
      <span style={{ width: 52, height: 52, borderRadius: "50%", background: "var(--ca-ink-2)", display: "grid", placeItems: "center", fontSize: 14, fontWeight: 600 }}>{done}/{total}</span>
    </div>
  );
}

function Spark({ vals }) {
  return (
    <svg viewBox="0 0 100 28" preserveAspectRatio="none" style={{ display: "block", width: "100%", height: 28, marginTop: 6 }}>
      <path d={sparkPath(vals)} fill="none" stroke="#E0B970" strokeWidth="1.6" />
    </svg>
  );
}

export default function Today() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [waiting, setWaiting] = useState([]);
  const [newOpps, setNewOpps] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [error, setError] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [approved, setApproved] = useState(null);
  const [doneCount, setDoneCount] = useState(0);
  const [filter, setFilter] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [edits, setEdits] = useState({});
  const [selected, setSelected] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const load = useCallback(() => {
    setError(false);
    setItems(null);
    setDoneCount(0);
    api.get("/prospects/today").then((r) => setItems(r.data)).catch(() => setError(true));
    api.get("/prospects/waiting").then((r) => setWaiting(r.data)).catch(() => setWaiting([]));
    api.get("/opportunities/search/pending-notice").then((r) => setNewOpps(r.data.has_new ? r.data : null)).catch(() => setNewOpps(null));
    api.get("/metrics").then((r) => setMetrics(r.data)).catch(() => setMetrics(null));
    api.get("/business-brain").then((r) => setApproved(!!(r.data && r.data.summary_approved))).catch(() => setApproved(true));
  }, []);

  useEffect(() => {
    load();
    window.addEventListener("ca:data-changed", load);
    return () => window.removeEventListener("ca:data-changed", load);
  }, [load]);

  const firstName = useMemo(() => (user?.name || "").trim().split(/\s+/)[0] || "", [user]);
  const chartVals = metrics?.chart ? metrics.chart.map((c) => c.value) : [];
  const filtered = useMemo(() => (items || []).filter((p) => !filter || p.channel === filter), [items, filter]);
  const total = (items?.length || 0) + doneCount;

  // v7: cada tarjeta es el paso del plan que vence hoy (o está atrasado).
  const stepOf = (p) => p.plan_step || null;
  const hasText = (p) => { const s = stepOf(p); return s ? !!ACTION_TEXT[s.action] : true; };
  const msgOf = (p) => (edits[p.id] !== undefined ? edits[p.id] : ((stepOf(p) ? stepOf(p).content : p.suggested_message) || ""));

  const copyAndOpen = (p) => {
    const txt = hasText(p) ? msgOf(p) : "";
    if (txt) { navigator.clipboard.writeText(txt); toast.success("Copiado. Pégalo en la red."); }
    const urls = { instagram: "https://instagram.com/", facebook: "https://facebook.com/", linkedin: "https://linkedin.com/" };
    const prof = p.profile_url || (p.ig_username ? `https://instagram.com/${p.ig_username}` : urls[p.channel]);
    if (prof) window.open(prof, "_blank", "noopener");
  };

  const markDone = async (p) => {
    setBusyId(p.id);
    try {
      const s = stepOf(p);
      if (s) await api.post(`/prospects/${p.id}/plan/done`, { step_id: s.id, content: ACTION_TEXT[s.action] ? msgOf(p) : null });
      else await api.post(`/prospects/${p.id}/advance`);
      setItems((cur) => cur.filter((x) => x.id !== p.id));
      setDoneCount((c) => c + 1);
      toast.success(`Hecho con ${p.name}. El siguiente paso ya está en su plan.`);
      window.dispatchEvent(new Event("ca:data-changed"));
    } catch (err) { toast.error(apiError(err)); } finally { setBusyId(null); }
  };

  const postpone = async (p) => {
    setBusyId(p.id);
    try {
      await api.post(`/prospects/${p.id}/plan/postpone`, { days: 1 });
      setItems((cur) => cur.filter((x) => x.id !== p.id));
      toast("Pospuesto a mañana.");
    } catch (err) { toast.error(apiError(err)); } finally { setBusyId(null); }
  };

  const openDrawer = (p) => { setSelected(p); setDrawerOpen(true); };
  const onDrawerUpdated = (p) => {
    setSelected(p);
    setItems((cur) => (cur || []).map((x) => (x.id === p.id ? { ...x, ...p } : x)));
  };
  const onDrawerDeleted = async (p) => {
    try { await api.delete(`/prospects/${p.id}`); setItems((cur) => (cur || []).filter((x) => x.id !== p.id)); toast(`${p.name} eliminado.`); }
    catch (err) { toast.error(apiError(err)); }
  };
  const closeDrawer = (o) => { setDrawerOpen(o); if (!o) load(); };

  const addToToday = async (p) => {
    try {
      await api.put(`/prospects/${p.id}`, { due_today: true });
      toast.success(`${p.name} está en tu agenda de hoy.`);
      load();
      window.dispatchEvent(new Event("ca:data-changed"));
    } catch (err) { toast.error(apiError(err)); }
  };

  const TIPS = [
    ["LO QUE ESTÁS APRENDIENDO", <><b className="text-[var(--ca-text)]">Los audios funcionan.</b> Los contactos a los que envías audio avanzan antes. Úsalos con oportunidades calientes.</>],
    ["INSTAGRAM TE RESPONDE MÁS", <>Tus mensajes por Instagram tienen mejor respuesta que por otras redes. <b className="text-[var(--ca-text)]">Prioriza ese canal</b> esta semana.</>],
  ];

  return (
    <div className="space-y-6">
      {/* Saludo + anillo */}
      <header className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between" data-testid="today-header">
        <div>
          <span className="ca-eyebrow">{todayLabel()}</span>
          <h1 className="mt-2" style={{ fontFamily: "var(--ca-f-display)", fontWeight: 600, fontSize: "clamp(30px,3.4vw,44px)", lineHeight: 1.02, letterSpacing: "-.02em", color: "var(--ca-text)" }}>
            {greeting()}{firstName ? <>, <span className="ca-serif ca-text-metal">{firstName}</span></> : ""}
          </h1>
          <p className="mt-2 text-sm text-[var(--ca-muted)]">
            {items && items.length > 0
              ? <>Hoy tienes <span className="font-semibold text-[var(--ca-text)] tnum">{items.length}</span> {items.length === 1 ? "acción" : "acciones"}{items[0]?.agenda_total > items.length ? <> (de <span className="tnum">{items[0].agenda_total}</span>; el resto, mañana)</> : ""}. Hazlas en tanda, por grupos.</>
              : "Tu agenda priorizada. Copia el mensaje, envíalo en la red y márcalo como hecho."}
          </p>
        </div>
        {(items && (items.length > 0 || doneCount > 0)) && (
          <div className="ca-glass flex items-center gap-4" style={{ padding: "14px 18px 14px 14px", borderRadius: 18 }}>
            <ProgressRing done={doneCount} total={total} />
            <div>
              <b className="block text-[15px] text-[var(--ca-text)]">Objetivo del día</b>
              <small className="text-[13px] text-[var(--ca-muted)]"><span className="tnum">{doneCount}</span> de <span className="tnum">{total}</span> acciones hechas</small>
            </div>
          </div>
        )}
      </header>

      {/* Primeros pasos (activación): se oculta solo al completar los 5. v26g: incluye «Revisa tu perfil en redes»,
          así que aquí el aviso solo sale para perfiles vinculados que necesitan arreglos (sin duplicar el paso). */}
      <FirstSteps />
      <ProfileNudge testId="today-profile-nudge" showUnlinked={false} />

      {/* KPIs */}
      {metrics && approved !== false && (
        <div className="grid grid-cols-2 gap-3.5 lg:grid-cols-4" data-testid="today-metrics">
          {CHIP_DEFS.map((d) => {
            const m = metrics.kpis?.[d.key] || {};
            const delta = m.delta ?? 0;
            return (
              <div key={d.key} data-testid={`today-chip-${d.key}`} style={{ padding: 18, borderRadius: 16, border: "1px solid var(--ca-line)", background: "var(--ca-surface)" }}>
                <small className="flex items-center justify-between text-[13px] text-[var(--ca-muted)]">
                  {d.label}
                  {delta !== 0 && <em className="not-italic font-mono text-[11px]" style={{ color: delta > 0 ? "var(--ca-gold-hi)" : "#F1707A" }}>{delta > 0 ? "+" : ""}{delta}</em>}
                </small>
                <b className="mt-2 block text-[34px] font-semibold tabular-nums" style={{ letterSpacing: "-.04em" }}>{m.value ?? 0}</b>
                <Spark vals={chartVals} />
              </div>
            );
          })}
        </div>
      )}

      {items === null && !error && <CardsSkeleton cards={2} />}
      {error && <ErrorState description="No hemos podido cargar tu agenda de hoy." onRetry={load} testId="today-error" />}

      {approved === false && (
        <div data-testid="today-gate" className="ca-card ca-card-gold p-8 text-center">
          <p className="text-base font-bold text-[var(--ca-text)]">Aprueba primero el resumen de tu negocio</p>
          <p className="mx-auto mt-1 max-w-md text-sm text-[var(--ca-muted)]">CaptAcción no empieza a recomendar perfiles ni estrategias hasta que apruebes el resumen en «Mi negocio».</p>
          <Link to="/app/brain" data-testid="today-gate-link" className="ca-btn-gold ca-btn-sm mt-5 inline-flex">
            Ir a Mi negocio <span className="ca-arr" aria-hidden>→</span>
          </Link>
        </div>
      )}

      {/* Tarjeta dorada de oportunidades */}
      {approved !== false && newOpps && (
        <Link to="/app/oportunidades" data-testid="today-new-opps" className="flex items-center gap-4" style={{ padding: "16px 18px", borderRadius: 16, border: "1px solid rgba(224,185,112,.3)", background: "linear-gradient(90deg,rgba(224,185,112,.10),rgba(224,185,112,.02))" }}>
          <span style={{ width: 44, height: 44, borderRadius: 12, display: "grid", placeItems: "center", background: "var(--ca-metal)", color: "var(--ca-gold-ink)", flex: "none", fontSize: 18 }}>✦</span>
          <div>
            <b className="block text-[var(--ca-text)]"><span className="tnum">{newOpps.count}</span> {newOpps.count === 1 ? "oportunidad nueva preparada" : "oportunidades nuevas preparadas"}</b>
            <small className="text-[13.5px] text-[var(--ca-muted)]">Candidatos con encaje alto listos para revisar.</small>
          </div>
          <span className="ca-btn-ghost ca-btn-sm" style={{ marginLeft: "auto" }}>Revisar →</span>
        </Link>
      )}

      {/* Columnas: agenda + aside */}
      {approved !== false && items && !error && (
        <div className="grid items-start gap-6 lg:grid-cols-[1fr_340px]">
          {/* Agenda */}
          <section>
            {items.length === 0 ? (
              <EmptyState icon={Check} title="Todo hecho por hoy" description="No te queda ninguna acción pendiente. Vuelve mañana o restaura los datos de ejemplo desde el menú lateral." testId="today-empty" />
            ) : (
              <>
              <div className="mb-3.5 flex items-center justify-between gap-3">
                <h2 className="ca-h2 flex items-center gap-2.5">Tu agenda de hoy <span className="ca-pill" style={{ padding: "3px 8px" }}>{filtered.length}</span></h2>
                <div className="ca-seg" role="group" aria-label="Filtrar por red">
                  {FILTERS.map((f) => (
                    <button key={f.k || "all"} data-testid={`agenda-filter-${f.k || "all"}`} onClick={() => setFilter(f.k)} className={filter === f.k ? "is-on" : ""}>{f.label}</button>
                  ))}
                </div>
              </div>

              {filtered.length === 0 ? (
                <div className="ca-card p-8 text-center text-sm text-[var(--ca-muted)]">Ninguna acción en esta red hoy.</div>
              ) : (
              <div className="grid gap-5">
                {TODAY_GROUPS.map(([gk, gl]) => {
                  const list = filtered.filter((p) => (p.today_group || "mensajes") === gk);
                  if (!list.length) return null;
                  return (
                  <div key={gk} className="grid gap-3.5" data-testid={`today-group-${gk}`}>
                    <small className="font-mono text-[11px] uppercase tracking-[.14em] text-[var(--ca-gold)]">{gl} · {list.length}</small>
                {list.map((p) => {
                  const st = stepOf(p);
                  const label = st ? (ACTION_LABEL[st.action] || "Acción") : (ACTION_META[p.next_action_type]?.label || "Mensaje directo");
                  const txt = hasText(p) ? msgOf(p) : "";
                  return (
                  <article key={p.id} data-testid={`today-card-${p.id}`} className="ca-card ca-card-hover animate-fade-up" style={{ padding: 18, display: "grid", gap: 14 }}>
                    {/* fila superior */}
                    <div className="flex flex-wrap items-center gap-3">
                      <span style={AV}>{initials(p.name)}</span>
                      <button className="text-left" onClick={() => openDrawer(p)} data-testid={`today-open-${p.id}`}>
                        <b className="block text-sm font-semibold hover:underline" style={{ letterSpacing: "-.01em" }}>{p.name}</b>
                        <small className="text-xs text-[var(--ca-subtle)]">{p.ig_username ? `@${p.ig_username}` : (p.company || "Sin empresa")} · {TYPE_META[p.opportunity_type]?.label || TYPE_META[p.objective]?.label || ""}</small>
                      </button>
                      {LEAD_LABEL[p.lead_label] && <span className="text-[11px] text-[var(--ca-subtle)]" title={p.label_reason || ""} data-testid={`today-lead-label-${p.id}`}>{LEAD_LABEL[p.lead_label].emoji} {LEAD_LABEL[p.lead_label].label}</span>}
                      {p.overdue_days > 0 && <span className="font-mono text-[11px]" style={{ color: "#F0B35B" }}>{dayLabel(st?.when)}</span>}
                      {p.fit_score ? <span className="ca-score ml-auto font-mono text-[11px]" style={{ padding: "3px 8px" }}>{p.fit_score} encaje</span> : null}
                    </div>

                    {/* línea de acción */}
                    <div className="text-[14.5px] font-medium">
                      {st ? st.title : (p.next_step || "Iniciar contacto")}
                      <small className="mt-0.5 block text-[12.5px] font-normal text-[var(--ca-subtle)]">
                        <NetIcon channel={p.channel} /> <span className="align-middle">{CHANNEL_META[p.channel]?.label} · {label}{st?.stage ? ` · ${STAGE_LABEL[st.stage] || ""}` : ""}</span>
                      </small>
                      {st?.todo?.length > 0 && <ul className="mt-2 list-disc pl-5 text-[13px] font-normal text-[var(--ca-muted)]">{st.todo.map((t, i) => <li key={i}>{t.text}</li>)}</ul>}
                    </div>

                    {/* texto */}
                    {editingId === p.id ? (
                      <textarea
                        data-testid={`today-edit-area-${p.id}`}
                        className="ca-input"
                        style={{ height: "auto", minHeight: 110, padding: 12, lineHeight: 1.5 }}
                        value={msgOf(p)}
                        onChange={(e) => setEdits((m) => ({ ...m, [p.id]: e.target.value }))}
                      />
                    ) : (txt ? (
                      <div className="text-sm">
                        <span className="leading-relaxed text-[var(--ca-text)]">{txt}</span>
                      </div>
                    ) : (st && ACTION_TEXT[st.action] ? <button className="ca-btn-ghost ca-btn-sm" style={{ justifySelf: "start" }} onClick={() => openDrawer(p)}>Preparar el texto en su ficha</button> : null))}

                    {/* botones */}
                    <div className="flex flex-wrap items-center gap-2">
                      <button onClick={() => copyAndOpen(p)} data-testid={`today-copy-${p.id}`} className="ca-btn-gold ca-btn-sm">{txt ? `Copiar y abrir ${CHANNEL_META[p.channel]?.label || ""}` : "Abrir su perfil"}</button>
                      <button onClick={() => markDone(p)} disabled={busyId === p.id} data-testid={`today-done-${p.id}`} className="ca-btn-ghost ca-btn-sm">✓ {busyId === p.id ? "…" : "Hecho"}</button>
                      <span style={{ flex: 1 }} />
                      {txt && <button onClick={() => setEditingId(editingId === p.id ? null : p.id)} data-testid={`today-edit-${p.id}`} className="ca-btn-ghost ca-btn-sm">{editingId === p.id ? "Listo" : "Editar"}</button>}
                      <button onClick={() => postpone(p)} disabled={busyId === p.id} data-testid={`today-postpone-${p.id}`} className="ca-btn-ghost ca-btn-sm">Posponer</button>
                    </div>
                  </article>
                  );
                })}
                  </div>
                  );
                })}
              </div>
              )}
              </>
            )}
          </section>

          {/* Aside */}
          <aside className="space-y-3.5">
            {waiting.length > 0 && (
              <>
                <div className="mb-3.5 flex items-center justify-between">
                  <h2 className="ca-h2 flex items-center gap-2.5">Te están esperando <span className="ca-pill" style={{ padding: "3px 8px" }}>{waiting.length}</span></h2>
                </div>
                <div data-testid="waiting-section" style={{ border: "1px solid var(--ca-line)", borderRadius: 18, background: "var(--ca-surface)", overflow: "hidden" }}>
                  {waiting.map((p, i) => (
                    <div key={p.id} data-testid={`waiting-item-${p.id}`} className="flex items-center gap-3" style={{ padding: "14px 16px", borderBottom: i < waiting.length - 1 ? "1px solid var(--ca-line)" : "none" }}>
                      <span style={AV}>{initials(p.name)}</span>
                      <div className="min-w-0">
                        <b className="block truncate text-sm">{p.name}</b>
                        <small className="text-[12.5px] text-[var(--ca-subtle)]">{statusBadge(p.status).label}</small>
                      </div>
                      <span className="ml-auto font-mono text-[11px]" style={{ color: "#E0B970" }}>{p.days_waiting} d</span>
                      <button onClick={() => addToToday(p)} data-testid={`waiting-add-${p.id}`} aria-label="Añadir a hoy" style={{ width: 32, height: 32, borderRadius: 9, border: "1px solid var(--ca-line-2)", display: "grid", placeItems: "center", color: "var(--ca-gold)", flex: "none" }}>+</button>
                    </div>
                  ))}
                </div>
              </>
            )}
            {TIPS.map(([k, body]) => (
              <div key={k} style={{ padding: 18, borderRadius: 18, border: "1px solid var(--ca-line)", background: "radial-gradient(circle at 100% 0%,rgba(224,185,112,.12),transparent 60%),var(--ca-surface)" }}>
                <small className="font-mono text-[10.5px] tracking-[.14em] text-[var(--ca-gold)]">{k}</small>
                <p className="mt-2 text-sm text-[var(--ca-muted)]">{body}</p>
              </div>
            ))}
          </aside>
        </div>
      )}
      <ProspectDrawer prospect={selected} open={drawerOpen} onOpenChange={closeDrawer} onUpdated={onDrawerUpdated} onDeleted={onDrawerDeleted} />
    </div>
  );
}

